import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Activity } from 'lucide-react';
import { CollapsibleSection } from './IntelligenceUIComponents';

export const BenchmarkingSection = ({ data, activeProfile }) => {
  const bm = data?.benchmarking;
  if (!bm) return null;

  const cc = bm.concacafComparison;
  const gt = bm.globalTier1Comparison;
  const kd = bm.keyDifferentiators || [];
  const coaches = cc.coaches || [];
  const globalCoaches = gt.coaches || [];
  
  const isHighlighted = (name) => name && name.includes(activeProfile === 'pochettino' ? 'Pochettino' : 'Supporting');
  
  return (
    <CollapsibleSection title="Benchmarking" icon={Activity} defaultOpen={false} badge="Competitive Analysis">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">CONCACAF National Team Coaches</h3>
          <div className="space-y-2">
            {coaches[0] && (
              <div className={`flex items-center justify-between p-3 rounded-lg ${isHighlighted(coaches[0].name) ? 'bg-primary/10 border border-primary/30' : 'bg-muted/20'}`}>
                <div className="flex items-center gap-2">
                  {coaches[0].rank && <span className="text-xs text-muted-foreground">#{coaches[0].rank}</span>}
                  <span className="text-sm font-medium">{coaches[0].name}</span>
                </div>
                <span className="text-lg font-bold">{coaches[0].score}</span>
              </div>
            )}
            {coaches[1] && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">#{coaches[1].rank}</span>
                  <span className="text-sm font-medium">{coaches[1].name}</span>
                </div>
                <span className="text-lg font-bold">{coaches[1].score}</span>
              </div>
            )}
            {coaches[2] && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">#{coaches[2].rank}</span>
                  <span className="text-sm font-medium">{coaches[2].name}</span>
                </div>
                <span className="text-lg font-bold">{coaches[2].score}</span>
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="p-3 bg-muted/20 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">Leadership Rank</p>
              <p className="text-xl font-bold text-primary">{cc.leadershipRankPercentile}%ile</p>
            </div>
            <div className="p-3 bg-muted/20 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">Brand Authority</p>
              <p className="text-xl font-bold text-primary">{cc.brandAuthorityPercentile}%ile</p>
            </div>
          </div>
        </div>

        {globalCoaches.length > 0 && (
          <div>
            <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Global Tier 1 Coaches</h3>
            <div className="space-y-2">
              {globalCoaches[0] && (
                <div className={`flex items-center justify-between p-3 rounded-lg ${isHighlighted(globalCoaches[0].name) ? 'bg-primary/10 border border-primary/30' : 'bg-muted/20'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">#{globalCoaches[0].rank}</span>
                    <span className="text-sm font-medium">{globalCoaches[0].name}</span>
                  </div>
                  <span className="text-lg font-bold">{globalCoaches[0].score}</span>
                </div>
              )}
              {globalCoaches[1] && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">#{globalCoaches[1].rank}</span>
                    <span className="text-sm font-medium">{globalCoaches[1].name}</span>
                  </div>
                  <span className="text-lg font-bold">{globalCoaches[1].score}</span>
                </div>
              )}
              {globalCoaches[2] && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">#{globalCoaches[2].rank}</span>
                    <span className="text-sm font-medium">{globalCoaches[2].name}</span>
                  </div>
                  <span className="text-lg font-bold">{globalCoaches[2].score}</span>
                </div>
              )}
            </div>
            {gt.globalPercentile && (
              <div className="mt-4 p-3 bg-stable/10 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Global Percentile</p>
                <p className="text-2xl font-bold text-stable">{gt.globalPercentile}%</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6">
        <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Key Differentiators</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {kd[0] && (
            <div className="flex items-center justify-between p-3 bg-stable/5 rounded-lg border border-stable/20">
              <span className="text-sm">{kd[0].factor}</span>
              <Badge className="bg-stable/20 text-stable">{kd[0].advantage}</Badge>
            </div>
          )}
          {kd[1] && (
            <div className="flex items-center justify-between p-3 bg-stable/5 rounded-lg border border-stable/20">
              <span className="text-sm">{kd[1].factor}</span>
              <Badge className="bg-stable/20 text-stable">{kd[1].advantage}</Badge>
            </div>
          )}
          {kd[2] && (
            <div className="flex items-center justify-between p-3 bg-stable/5 rounded-lg border border-stable/20">
              <span className="text-sm">{kd[2].factor}</span>
              <Badge className="bg-stable/20 text-stable">{kd[2].advantage}</Badge>
            </div>
          )}
          {kd[3] && (
            <div className="flex items-center justify-between p-3 bg-stable/5 rounded-lg border border-stable/20">
              <span className="text-sm">{kd[3].factor}</span>
              <Badge className="bg-stable/20 text-stable">{kd[3].advantage}</Badge>
            </div>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default BenchmarkingSection;
